import { useEffect, useCallback, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  googlePayCredentialsAtom,
  venueAtom,
  userInfoAtom,
  paymentMethodsAtom,
  applePayUserAtom,
  canPayWithGooglePayAtom,
  canPayWithApplePayAtom,
} from '@/state/global'
import { PaymentDataAtom, PaymentData } from '@/state/payment'
import { PaymentMethodInputTypes, GOOGLE_PAY_ENV, GOOGLE_PAY_ALLOWED_CARD_NETWORKS } from '@/utils/constants'
import { ApplePayUser } from '@/utils/types'

// Module-level singletons — persist across component unmount/remount cycles.
// Only one TEConnect instance should ever exist per session.
let globalTeConnect: any = null
let globalTecPrInterface: any = null
let globalIsInitialized = false
let globalListenerRegistered = false
let globalInitGeneration = 0

// Module-level pending promise resolution — shared across all hook instances so that
// the single listenFor callback always resolves the correct promise regardless of
// which component called handleMagensaGooglePayClick.
let globalPendingResolve: ((result: MagensaGooglePayResult) => void) | null = null
let globalPendingReject: ((error: any) => void) | null = null

// Points to the most recently mounted instance's confirm-token handler.
// Updated via useEffect so the single listener always calls a fresh closure
// with up-to-date Jotai atom values (storedUser, paymentMethods, etc.).
let globalConfirmTokenHandler: ((data: any) => void) | null = null

const HIDDEN_BUTTON_ID = 'magensa-gpay-hidden-button'

type MagensaGooglePayResult = {
  paymentMethod: PaymentData
  paymentUser: ApplePayUser
}

type UseMagensaGooglePayReturn = {
  handleMagensaGooglePayClick: (totalAmount?: string) => Promise<MagensaGooglePayResult>
  isMagensaGooglePaySupported: boolean
  isMagensaGooglePayInitialized: boolean
}

// Imperatively create and append the hidden container to document.body once.
// This keeps it outside of React's control so re-renders can never replace the
// DOM node or the SDK button mounted inside it. Multiple hook instances safely
// share this single element.
function ensureHiddenContainer(): HTMLElement {
  let container = document.getElementById(HIDDEN_BUTTON_ID)
  if (!container) {
    container = document.createElement('div')
    container.id = HIDDEN_BUTTON_ID
    container.setAttribute('aria-hidden', 'true')
    Object.assign(container.style, {
      position: 'absolute',
      left: '-9999px',
      top: '0',
      width: '150px',
      height: '50px',
      overflow: 'hidden',
    })
    document.body.appendChild(container)
  }
  return container
}

export function useMagensaGooglePay(): UseMagensaGooglePayReturn {
  const venue = useAtomValue(venueAtom)
  const googlePayCredentials = useAtomValue(googlePayCredentialsAtom)
  const canPayWithGooglePay = useAtomValue(canPayWithGooglePayAtom)
  const canPayWithApplePay = useAtomValue(canPayWithApplePayAtom)
  const storedUser = useAtomValue(userInfoAtom)
  const setApplePayUser = useSetAtom(applePayUserAtom)
  const [paymentMethods] = useAtom(paymentMethodsAtom)
  const [, setPaymentMethod] = useAtom(PaymentDataAtom)

  const [isInitialized, setIsInitialized] = useState(globalIsInitialized)

  const isMagensaGooglePaySupported =
    canPayWithGooglePay && !canPayWithApplePay && venue?.payment_processor === 'magensa_unigate' && !!venue?.google_pay_enabled

  const handleConfirmToken = useCallback(
    (data: any) => {
      const { tokenDetails, error } = data

      if (error) {
        console.error('[MagensaGPay] confirm-token error:', error)
        globalPendingReject?.(error)
        globalPendingResolve = null
        globalPendingReject = null
        return
      }

      const cardInfo = tokenDetails?.additionalData?.info
      const billingName: string = cardInfo?.billingAddress?.name ?? ''
      const nameParts = billingName.split(' ')

      let first_name = ''
      let last_name = ''
      if (nameParts.length === 2) {
        first_name = nameParts[0]
        last_name = nameParts[1]
      } else if (nameParts.length >= 3) {
        if (nameParts[1].length === 1) {
          first_name = nameParts[0]
          last_name = nameParts[nameParts.length - 1]
        } else {
          first_name = `${nameParts[0]} ${nameParts[1]}`
          last_name = nameParts[nameParts.length - 1]
        }
      }

      const userName = {
        first_name: first_name || storedUser?.first_name || '',
        last_name: last_name || storedUser?.last_name || '',
      }

      const paymentMethod: PaymentData = {
        network: (cardInfo?.cardNetwork ?? '').toLowerCase(),
        name: `${userName.last_name.toUpperCase()}/${userName.first_name.toUpperCase()}`,
        pan_mask: `************${cardInfo?.cardDetails ?? ''}`,
        input_mode: PaymentMethodInputTypes.MAGENSA_UNIGATE_GOOGLE_PAY_WEB,
        card_token: tokenDetails?.token ?? '',
      }

      let cleanPhone =
        (tokenDetails?.additionalData?.info?.billingAddress?.phoneNumber?.replace(/(?!^\+)\D/g, '') as string) ?? ''
      if (!cleanPhone.startsWith('+')) {
        cleanPhone = ''
      }

      const paymentUser: ApplePayUser = {
        first_name: userName.first_name,
        last_name: userName.last_name,
        email: tokenDetails?.additionalData?.email ?? '',
        phone: cleanPhone,
      }

      localStorage.setItem('paymentMethods', JSON.stringify([paymentMethod, ...paymentMethods]))
      setPaymentMethod(paymentMethod)

      if (!storedUser?.phone) {
        setApplePayUser(paymentUser)
      }

      globalPendingResolve?.({ paymentMethod, paymentUser })
      globalPendingResolve = null
      globalPendingReject = null
    },
    [storedUser, paymentMethods]
  )

  // Keep the module-level handler pointer current so the single registered listener
  // always invokes a fresh closure with up-to-date atom values.
  useEffect(() => {
    globalConfirmTokenHandler = handleConfirmToken
  }, [handleConfirmToken])

  // Initialize TEConnect and mount button into the hidden container.
  // Runs when credentials are available. Creates the singleton instance once;
  // creates a fresh interface on each mount (per TEConnect best practice).
  useEffect(() => {
    if (!isMagensaGooglePaySupported) {
      return
    }
    if (!venue?.magensa_unigate_teconnect_public_key) {
      console.warn('[MagensaGPay] init skipped — magensa_unigate_teconnect_public_key is missing from venue')
      return
    }
    if (!googlePayCredentials?.googleMerchantId) {
      console.warn('[MagensaGPay] init skipped — googleMerchantId is missing from googlePayCredentials', googlePayCredentials)
      return
    }

    globalIsInitialized = false
    setIsInitialized(false)

    const myGeneration = ++globalInitGeneration

    let isMounted = true
    const isSuperseded = () => myGeneration !== globalInitGeneration || !isMounted

    const initTEConnect = async () => {
      try {
        const [{ createTEConnect }, { TeConnectJs }] = await Promise.all([
          import('@magensa/te-connect'),
          import('@magensa/te-connect-js'),
        ])
        if (isSuperseded()) return

        // Yield to the macrotask queue so that effects from other hook instances
        // have a chance to fire and increment globalInitGeneration. Without this,
        // the first init can race ahead (import resolves → synchronous SDK work)
        // before later effects even start, creating duplicate SDK resources.
        await new Promise((r) => setTimeout(r, 0))
        if (isSuperseded()) return

        // Create the TEConnect instance once for the session
        if (!globalTeConnect) {
          const teConnectInstance = createTEConnect(venue.magensa_unigate_teconnect_public_key as string, {
            tecPaymentRequest: {
              googleMerchantId: googlePayCredentials.googleMerchantId as string,
            },
          })
          globalTeConnect = new TeConnectJs(teConnectInstance)
        }

        if (isSuperseded()) return

        // Reuse the existing payment request interface if one exists.
        // Creating multiple interfaces from the same TEConnect instance causes
        // canMakePayments() to return null on subsequent interfaces.
        const googlePaymentRequest = {
          allowedCardNetworks: GOOGLE_PAY_ALLOWED_CARD_NETWORKS,
          merchantName: venue.name ?? '',
          gatewayId: googlePayCredentials.googleGatewayId ?? 'magensa',
          transactionInfo: {
            allowedAuthMethods: ['PAN_ONLY'],
            countryCode: 'US',
            currencyCode: 'USD',
            totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
          },
          environment: GOOGLE_PAY_ENV,
          emailRequired: true,
          billingAddressRequired: true,
          billingAddressParameters: {
            phoneNumberRequired: true,
            format: 'MIN',
          },
        }

        if (!globalTecPrInterface) {
          globalTecPrInterface = globalTeConnect.createTecPaymentRequest(googlePaymentRequest)
          globalListenerRegistered = false
        }

        // Call canMakePayments() to initialize the TEConnect SDK internals.
        // We don't use this result for availability — canPayWithGooglePayAtom (set via
        // native isReadyToPay() in _app.tsx) is the source of truth.
        await globalTecPrInterface.canMakePayments()

        if (isSuperseded()) return

        if (!globalListenerRegistered) {
          globalTecPrInterface.listenFor('confirm-token', (data: any) => {
            globalConfirmTokenHandler?.(data)
          })
          globalListenerRegistered = true
        }

        const buttonOptions = {
          googlePayOptions: {
            buttonColor: 'black',
            buttonType: 'plain',
            buttonSizeMode: 'fill',
          },
        }

        const container = ensureHiddenContainer()
        const existingBtn = container.querySelector('button') ?? container.querySelector('[role="button"]')

        if (!existingBtn) {
          globalTecPrInterface.mountTecPrButton(HIDDEN_BUTTON_ID, buttonOptions)

          // mountTecPrButton is async internally — the Google Pay button is
          // created asynchronously by the SDK. Poll until the button actually
          // appears in the DOM before marking as initialized.
          for (let i = 0; i < 50; i++) {
            if (isSuperseded()) return
            const btn = container.querySelector('button') ?? container.querySelector('[role="button"]')
            if (btn) break
            await new Promise((r) => setTimeout(r, 100))
          }
        }

        if (isSuperseded()) return

        const finalBtn = container.querySelector('button') ?? container.querySelector('[role="button"]')
        if (!finalBtn) {
          console.error('[MagensaGPay] button not found after mount/poll — init incomplete')
          return
        }

        globalIsInitialized = true
        if (isMounted) setIsInitialized(true)
      } catch (err) {
        console.error('[MagensaGPay] init failed:', err)
      }
    }

    initTEConnect()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMagensaGooglePaySupported, venue?.magensa_unigate_teconnect_public_key, googlePayCredentials?.googleMerchantId])

  const handleMagensaGooglePayClick = useCallback((totalAmount?: string): Promise<MagensaGooglePayResult> => {
    return new Promise((resolve, reject) => {
      if (!globalIsInitialized || !globalTecPrInterface) {
        const msg = 'Magensa TEConnect Google Pay is not initialized'
        console.error('[MagensaGPay]', msg)
        reject(new Error(msg))
        return
      }

      // Reject any in-flight request before starting a new one
      if (globalPendingReject) {
        console.warn('[MagensaGPay] superseding an existing in-flight payment request')
        globalPendingReject(new Error('Payment request superseded'))
      }

      globalPendingResolve = resolve
      globalPendingReject = reject

      const container = document.getElementById(HIDDEN_BUTTON_ID)
      const btn =
        (container?.querySelector('button') as HTMLElement | null) ??
        (container?.querySelector('[role="button"]') as HTMLElement | null) ??
        (container?.firstElementChild as HTMLElement | null)

      if (!btn) {
        const msg = 'TEConnect Google Pay button not found in DOM'
        console.error('[MagensaGPay]', msg)
        reject(new Error(msg))
        globalPendingResolve = null
        globalPendingReject = null
        return
      }

      // Click synchronously — still within the user gesture activation window
      // from the original button tap (Open Tab / Submit Order / Pay Now)
      btn.click()
    })
  }, [])

  return {
    handleMagensaGooglePayClick,
    isMagensaGooglePaySupported,
    isMagensaGooglePayInitialized: isInitialized,
  }
}
